import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import QuestionHeader from "../../../components/common/questionHeader";
import QuestionSubHeader from "../../../components/common/questionSubHeader";
import ParticipantInitialInventoryQuestions from "../../../components/participants/ictInitialInventory/ictInitialInventoryDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  IctDetailsErrorSelector,
  IctDetailsLoadingSelector,
  IctInventoryDataSelector,
  IctInventoryPartialAnswersDataSelector,
} from "../../../redux/selectors/ictDetailsSelector";
import { IctDetailsAction } from "../../../redux/action";
import { useNavigate, useParams } from "react-router-dom";
import {
  setIctDetailsError,
  setIctInventoryData,
} from "../../../redux/slice/ictDetailsSlice";
import Loader from "../../../components/common/spinner/WhiteSpinner";
import ErrorPage from "../../../components/errors/errorPage";

function ParticipantInitialInventoryDetailsPage() {
  const navigate = useNavigate();
  const { inventory_id } = useParams();
  const XAM_TYPE = 4;
  const { t } = useTranslation();
  const ictInitialInventoryData = useSelector(IctInventoryDataSelector);
  const ictDetailsErrorData = useSelector(IctDetailsErrorSelector);
  const isLoading = useSelector(IctDetailsLoadingSelector);
  const partialAnswers = useSelector(
    IctInventoryPartialAnswersDataSelector,
  );
  const dispatch = useDispatch();
  const blockPartialSaveRef = useRef(false);

  useEffect(() => {
    dispatch(setIctDetailsError({}));
    dispatch(setIctInventoryData({}));
    dispatch(IctDetailsAction.getIctInventoryDetailsAsync(inventory_id, t));
    dispatch(IctDetailsAction.getIctInventoryPartialAnswersAsync(inventory_id, t));
  }, [inventory_id, dispatch, t]);
  useEffect(() => {
    if (ictDetailsErrorData?.data?.error?.error_code === 4043) {
      navigate(`/participants/ict/initial-inventories/${inventory_id}/results`);
    }
  }, [ictDetailsErrorData, inventory_id, navigate]);

  // add the answer to the inventory api
  const setAddAnswer = useCallback(
    (inv_id, data) => {
      blockPartialSaveRef.current = true;
      dispatch(
        IctDetailsAction.addIctInventoryAnswerAsync(inv_id, data, t, navigate),
      ).then((success) => {
        if (!success) {
          blockPartialSaveRef.current = false;
        }
      });
    },
    [dispatch, navigate, t],
  );

  // add the partial answer to the inventory api
  const setAddPartialAnswer = useCallback(
    (inv_id, data) => {
      if (blockPartialSaveRef.current) {
        return;
      }
      dispatch(
        IctDetailsAction.addIctInventoryPartialAnswersAsync(inv_id, data, t),
      );
    },
    [dispatch, t],
  );
  // set the answer page for the initial inventory api
  const setAnswerPage = useCallback(
    (dataToSave) =>
      dispatch(
        IctDetailsAction.UpdateIctInventoryAnswerPageAsync(
          inventory_id,
          dataToSave,
          t,
        ),
      ),
    [dispatch, inventory_id, t],
  );

  const getCurrentPage = () => {
    return ictInitialInventoryData?.current_page;
  };

  return (
    <>
      {isLoading && <Loader />}
      {ictDetailsErrorData?.status === 404 ||
      (ictDetailsErrorData?.status === 403 &&
        ictDetailsErrorData?.data?.error?.error_code !== 4043) ? (
        <>
          <ErrorPage status={ictDetailsErrorData?.status} />
        </>
      ) : (
        <div className="main_bg d-flex flex-column min-vh-100">
          <section className="mb-5">
            <QuestionHeader type={XAM_TYPE} />
            <QuestionSubHeader
              data={ictInitialInventoryData}
              type={XAM_TYPE}
              serviceType="ict"
            />
            <ParticipantInitialInventoryQuestions
              setAnswer={setAddAnswer}
              data={ictInitialInventoryData}
              PartialAnswers={partialAnswers?.data}
              setAddPartialAnswer={setAddPartialAnswer}
              setAnswerPage={setAnswerPage}
              currentPage={getCurrentPage()}
            />
          </section>
        </div>
      )}
    </>
  );
}

export default ParticipantInitialInventoryDetailsPage;
